import React from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { 
  LayoutDashboard, Users, FileBox, FileBarChart, Settings, 
  LogOut, BarChart3, Database, ChevronLeft, Menu 
} from "lucide-react";
import { NavLink } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

export function AdminLayout() {
  const { isAuthenticated, isLoading, logout } = useAdminAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/admin/login");
    }
  }, [isAuthenticated, isLoading, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#0F111A]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-[#0F111A] text-white">
      {/* Mobile sidebar toggle */}
      <div className="lg:hidden fixed top-4 left-4 z-30">
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" className="p-2 text-gray-300 hover:text-white">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="bg-[#161A27] border-gray-800 p-0">
            <AdminSidebar onLogout={handleLogout} />
          </SheetContent>
        </Sheet>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:block w-64 bg-[#161A27] border-r border-gray-800 overflow-y-auto">
        <AdminSidebar onLogout={handleLogout} />
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-y-auto">
        <Outlet />
      </div>
    </div>
  );
}

interface AdminSidebarProps {
  onLogout: () => void;
}

function AdminSidebar({ onLogout }: AdminSidebarProps) {
  const navItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin/dashboard" },
    { icon: Users, label: "Users", path: "/admin/users" },
    { icon: FileBox, label: "Collections", path: "/admin/collections" },
    { icon: FileBarChart, label: "Analytics", path: "/admin/analytics" },
    { icon: Database, label: "Data Export", path: "/admin/export" },
    { icon: Settings, label: "Settings", path: "/admin/settings" },
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="p-6">
        <div className="flex items-center space-x-2">
          <BarChart3 className="h-7 w-7 text-blue-500" />
          <h1 className="text-xl font-bold">Admin Dashboard</h1>
        </div>
      </div>
      
      <nav className="flex-1 px-3 py-3">
        <ul className="space-y-1">
          {navItems.map((item) => (
            <li key={item.path}>
              <NavLink 
                to={item.path} 
                className={({ isActive }) => `
                  flex items-center px-4 py-2 rounded-lg text-sm
                  ${isActive 
                    ? 'bg-blue-500/10 text-blue-500 border border-blue-500/20'
                    : 'text-gray-300 hover:bg-white/5 hover:text-white'
                  }
                `}
              >
                <item.icon className="h-5 w-5 mr-3" />
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      
      <div className="p-4 border-t border-gray-800">
        <Button 
          variant="ghost" 
          className="w-full justify-start text-gray-400 hover:text-white hover:bg-red-500/10"
          onClick={onLogout}
        >
          <LogOut className="h-5 w-5 mr-3" />
          Logout
        </Button>
      </div>
    </div>
  );
}
